import { CheckCircle2, Flag, MapPin, Ruler } from 'lucide-react';
import type { Competition } from '../../data/competitions';
import { lineArenas } from '../../data/arenas';
import type { LineArena } from '../../data/arenas';

interface Props {
  comp: Competition;
  arenaId: string;
  setArenaId: (id: string) => void;
}

const PAD = 14;
const VIEW_W = 220;
const VIEW_H = 150;

/** Straight-segment top-view preview of the arena path, scaled into a fixed SVG viewBox. */
function arenaPreviewPath(arena: LineArena): { d: string; start: [number, number]; finish: [number, number] } {
  const xs = arena.waypoints.map((p) => p[0]);
  const zs = arena.waypoints.map((p) => p[1]);
  const minX = Math.min(...xs);
  const maxX = Math.max(...xs);
  const minZ = Math.min(...zs);
  const maxZ = Math.max(...zs);
  const spanX = Math.max(maxX - minX, 0.001);
  const spanZ = Math.max(maxZ - minZ, 0.001);
  const scale = Math.min((VIEW_W - PAD * 2) / spanX, (VIEW_H - PAD * 2) / spanZ);
  const offX = (VIEW_W - spanX * scale) / 2;
  const offZ = (VIEW_H - spanZ * scale) / 2;

  const project = ([x, z]: [number, number]): [number, number] => [
    offX + (x - minX) * scale,
    offZ + (z - minZ) * scale,
  ];

  const points = arena.waypoints.map(project);
  const d = points.map(([x, y], i) => `${i === 0 ? 'M' : 'L'}${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
  return { d, start: points[0], finish: points[points.length - 1] };
}

export default function ArenaTab({ comp, arenaId, setArenaId }: Props) {
  return (
    <div className="space-y-5">
      <div className="flex items-center gap-2 text-sm font-bold uppercase tracking-wide text-slate-400">
        <MapPin className="size-4" />
        Choose your track
      </div>
      <p className="max-w-2xl text-sm leading-relaxed text-slate-400">
        Practice on the test tracks first, then run the official arenas — the same ones used at the
        competition. Your choice is saved and used by the Simulate tab.
      </p>

      <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        {lineArenas.map((arena) => {
          const { d, start, finish } = arenaPreviewPath(arena);
          const selected = arena.id === arenaId;
          return (
            <button
              key={arena.id}
              type="button"
              onClick={() => setArenaId(arena.id)}
              className={`relative overflow-hidden rounded-2xl border p-4 text-left transition ${
                selected
                  ? `${comp.border} bg-white/[0.06]`
                  : 'border-white/10 bg-white/[0.03] hover:border-white/20 hover:bg-white/[0.05]'
              }`}
            >
              {selected && (
                <CheckCircle2 className={`absolute right-3 top-3 size-5 ${comp.text}`} />
              )}
              <svg viewBox={`0 0 ${VIEW_W} ${VIEW_H}`} className="w-full rounded-lg bg-navy-950/60">
                <path d={d} fill="none" stroke="#334155" strokeWidth={10} strokeLinecap="round" strokeLinejoin="round" />
                <path d={d} fill="none" stroke="#e2e8f0" strokeWidth={3} strokeLinecap="round" strokeLinejoin="round" />
                <circle cx={start[0]} cy={start[1]} r={4} fill="#22c55e" />
                <circle cx={finish[0]} cy={finish[1]} r={4} fill="#ef4444" />
              </svg>

              <div className="mt-3 flex items-center justify-between gap-2">
                <span className="text-sm font-extrabold text-slate-100">{arena.name}</span>
                <span
                  className={`shrink-0 rounded-full px-2 py-0.5 text-[10px] font-bold uppercase tracking-wide ring-1 ${
                    arena.kind === 'Official'
                      ? 'bg-amber-400/10 text-amber-200 ring-amber-400/30'
                      : 'bg-white/5 text-slate-400 ring-white/10'
                  }`}
                >
                  {arena.kind}
                </span>
              </div>
              <p className="mt-1 text-xs leading-relaxed text-slate-500">{arena.description}</p>
              <div className="mt-2 flex flex-wrap items-center gap-3 text-[11px] font-semibold text-slate-500">
                <span className="inline-flex items-center gap-1">
                  <Flag className="size-3" />
                  {arena.difficulty}
                </span>
                <span className="inline-flex items-center gap-1">
                  <Ruler className="size-3" />
                  {(arena.lineWidthCm ?? comp.rulePack.track?.lineWidthCm ?? 2.5)} cm line
                </span>
              </div>
            </button>
          );
        })}
      </div>
    </div>
  );
}
